<template>
    <ClModal
        v-model="showModal"
        class="modal-modify-permissions"
        maxWidth="600px"
    >
        <v-card>
            <v-card-title class="modal-card-title primary white--text">
                <h3>Role Assignment</h3>
                <v-spacer/>
                <ClButton
                    icon
                    aria-label="Close Modal"
                    color="white"
                    @click="showModal = false"
                >
                    <v-icon>mdi-close</v-icon>
                </ClButton>
            </v-card-title>
            <v-card-text class="modal-modify-permissions__card-text">
                <v-row>
                    <v-col>
                        <!-- Location Selection -->
                        <v-select
                            v-if="modalTypeIsNewLocation"
                            v-model="models.location"
                            :items="locationList"
                            label="Select a Location"
                            placeholder="Select a Location"
                            itemText="friendly"
                            itemValue="id"
                            returnObject
                            dense
                            outlined
                        />
                        <!-- Available Roles -->
                        <ClTableFilterable
                            v-if="hasPossibleRoles"
                            :items="selectableRoles"
                            :filter.sync="possibleRoles.table.filter"
                            :headers="possibleRoles.table.headers"
                            :selected.sync="models.checkedRoles"
                            :itemsPerPage="-1"
                            filterLabel="Filter Roles..."
                            noDataText="This user has all possible roles."
                            dense
                            hideAdd
                            showSelect
                        />
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="modal-modify-permissions__card-actions">
                <v-row>
                    <v-spacer/>
                    <ClButton
                        title="Add a role to a location"
                        aria-label="Add a role to a location"
                        :disabled="!canAssignRoles"
                        :rounded="false"
                        @click="assignButtonHandler()"
                    >
                        Assign
                    </ClButton>
                </v-row>
            </v-card-actions>
        </v-card>
    </ClModal>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Api
import PermissionApi from '@/api/PermissionApi';

// Utils
import ValidationUtil from '@/utils/ValidationUtil';

const defaults = {
    models: {
        /** @returns { Array } */
        checkedRoles: () => ( [] ),
        /** @returns { Object } */
        location: () => ( {
            id: null,
        } ),
    },
};

export default {
    name: 'ModalModifyPermission',
    props: {
        value: {
            type: Boolean,
            required: true,
        },
        modalType: {
            type: String,
            required: true,
            validator: ( value ) => [ '', 'NewLocation', 'ExistingLocation' ].includes( value ),
        },
        targetLocation: {
            type: Object,
            required: false,
            default: () => ( { id: null, friendly: 'Placeholder' } ),
        },
        locationList: {
            type: Array,
            required: true,
        },
        currentPermissionsList: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            models: {
                /** @type { Array } */
                checkedRoles: defaults.models.checkedRoles(),
                /** @type { Object } */
                location: defaults.models.location(), // locally selectable location ( for NewLocation modalType )
            },

            possibleRoles: {
                list: [],
                inFlight: false,
                table: {
                    filter: '',
                    headers: [
                        {
                            text: 'Role',
                            value: 'friendly',
                        },
                    ],
                },
            },
        };
    },
    computed: {
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
        } ),
        ...mapGetters( {
            websiteId: 'Website/id',
        } ),
        showModal: {
            get() {
                return this.value;
            },
            set( value ) {
                this.$emit( 'input', value );
            },
        },
        /** @returns { Boolean } */
        modalTypeIsNewLocation() {
            return this.modalType === 'NewLocation';
        },
        /** @returns { Boolean } */
        modalTypeIsExistingLocation() {
            return this.modalType === 'ExistingLocation';
        },
        /** @returns { Boolean } */
        hasLocationList() {
            return this.locationList.length > 0;
        },
        /** @returns { Boolean } */
        hasCurrentPermissionsList() {
            return Object.values( this.currentPermissionsList ).length > 0;
        },
        /** @returns { Boolean } */
        hasTargetLocation() {
            return this.targetLocation.id !== null;
        },
        /** @returns { Boolean } */
        hasLocalLocation() {
            return this.models.location.id !== null;
        },
        /** @returns { Boolean } */
        hasLocationSelected() {
            return !!( this.hasTargetLocation || this.hasLocalLocation );
        },
        /** @returns { Boolean } */
        hasPossibleRoles() {
            return this.possibleRoles.list.length > 0;
        },
        /** @returns { Boolean } */
        hasCheckedRoles() {
            return this.models.checkedRoles.length > 0;
        },
        /** @returns { Boolean } */
        canAssignRoles() {
            return this.hasLocationSelected && this.hasCheckedRoles;
        },
        /** @returns { String } */
        displayedLocationName() {
            return this.hasTargetLocation
                ? this.targetLocation.friendly
                : this.models.location.friendly || 'Select a location';
        },
        /** @returns { Array } */
        currentRolesForSelectedLocation() {
            return this.hasLocationList
                && this.hasCurrentPermissionsList
                && this.hasLocationSelected
                && this.hasPossibleRoles
                && this.currentPermissionsList[ this.targetLocation.id || this.models.location.id ]
                ? this.currentPermissionsList[ this.targetLocation.id || this.models.location.id ].authRoleDTOS
                : [];
        },
        /**
         * Return an array of roles with already-assigned roles filtered out
         * @returns { Object[] }
         */
        selectableRoles() {
            return this.possibleRoles.list
                .filter( ( possibleRole ) => (
                    !new Set( this.currentRolesForSelectedLocation.map( ( existingRole ) => existingRole.name ) )
                        .has( possibleRole.name )
                ) );
        },
    },
    watch: {
        showModal() {
            this.models.checkedRoles = defaults.models.checkedRoles();
            this.models.location = defaults.models.location();
            this.possibleRoles.list = [];
            if ( this.showModal ) {
                if ( this.hasTargetLocation && this.modalTypeIsExistingLocation ) this.getAllAvailableRoles();
            }
        },
        // eslint-disable-next-line object-shorthand
        'models.location'() {
            if ( this.hasLocalLocation ) this.getAllAvailableRoles();
        },
    },
    methods: {
        async getAllAvailableRoles() {
            this.possibleRoles.inFlight = true;
            try {
                const { data } = await PermissionApi.getAllMailRoles( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId, // TODO: change this to the selected location's websiteId
                    userAccountId: this.userAccountId,
                } );
                this.possibleRoles.list = ValidationUtil.arrayHasItems( data.authRoles )
                    ? data.authRoles.sort( ( a, b ) => ( a.friendly > b.friendly ? 1 : -1 ) )
                    : [];
            } catch ( error ) {
                console.error( 'Error in getAllAvailableRoles: ', error );
            } finally {
                this.possibleRoles.inFlight = false;
            }
        },

        assignButtonHandler() {
            if ( this.canAssignRoles ) {
                this.$emit( 'assignRoles', {
                    locationId: this.models.location.id || this.targetLocation.id,
                    roles: this.models.checkedRoles,
                } );
            }
        },
    },
};
</script>

<style scoped lang="scss">
.modal-modify-permissions {
    .modal-card-title {
        .header-prefix-text {
            font-size: 1rem;
            text-decoration: underline;
            margin-bottom: 0.5rem;
            color: #696969;
        }
        .location-name {
            font-size: 1.35rem;
        }
    }
}
.v-dialog {
    >.v-card {
        >.v-card__text {
            margin-top: 2rem;
            display: flex;
            flex-direction: row;
            justify-content: center;
        }
        >.v-card__actions {
            padding-bottom: 1rem;
        }
    }
}
</style>
